/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { computed, get } from '@ember/object';
import { mapBy } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import LineItemAdjustmentMode from 'linced-app/enums/line-item-adjustment-mode';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import config from 'linced-app/config/environment';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import {
  rejectBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class FinanceService extends Service {
  // Services
  @service
  features;

  @service
  fetch;

  @service
  session;

  @service
  store;

  @computed('session.school.jurisdiction')
  get allPaymentMethods() {
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.AU_NSW:
        return [
          { value: PaymentMethod.CASH, label: 'Cash' },
          { value: PaymentMethod.EFTPOS, label: 'EFTPOS' },
          { value: PaymentMethod.CHEQUE, label: 'Cheque' },
          { value: PaymentMethod.MONEY_ORDER, label: 'Money Order' },
          { value: PaymentMethod.ONLINE, label: 'Online' },
          { value: PaymentMethod.EXTERNAL, label: 'Third Party' },
        ];
    }

    // Otherwise, return the enum values - without Money Order, as that's
    // specific to NSW.
    return rejectBy(PaymentMethod.values, 'value', PaymentMethod.MONEY_ORDER);
  }

  @computed('session.school.jurisdiction')
  get availablePaymentMethods() {
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.AU_NSW:
        return [
          { value: PaymentMethod.CASH, label: 'Cash' },
          { value: PaymentMethod.CHEQUE, label: 'Cheque' },
          { value: PaymentMethod.MONEY_ORDER, label: 'Money Order' },
          { value: PaymentMethod.EFTPOS, label: 'EFTPOS' },
          { value: PaymentMethod.EXTERNAL, label: 'Third Party' },
        ];
    }
    return [
      { value: PaymentMethod.CASH, label: 'Cash' },
      { value: PaymentMethod.EFTPOS, label: 'EFTPOS' },
      { value: PaymentMethod.CHEQUE, label: 'Cheque' },
      { value: PaymentMethod.CREDIT_CARD, label: 'Credit Card' },
      { value: PaymentMethod.INTERNET, label: 'Internet Banking' },
      { value: PaymentMethod.JOURNAL, label: 'Journal' },
      { value: PaymentMethod.EXTERNAL, label: 'External' },
    ];
  }

  physicalPaymentMethods = Object.freeze([
    PaymentMethod.CASH,
    PaymentMethod.CHEQUE,
    PaymentMethod.MONEY_ORDER,
  ]);

  getPaymentMethodLabel(value) {
    let method = findBy(this.allPaymentMethods, 'value', value);
    if (isEmpty(method)) {
      // Try falling back to the full enum, in case of weird data.
      return PaymentMethod.getLabel(value);
    }
    return method.label;
  }

  @tracked
  tills = null;
  @tracked
  activeTill = null;
  @tracked
  sessions = null;

  // Intermediary CPs so the @each dependent keys below stay one level deep.
  @mapBy('sessions', 'till')
  sessionTills;

  @mapBy('sessions', 'person')
  sessionPeople;

  @computed(
    'sessionTills.@each.id',
    'sessionPeople.@each.id',
    'session.person.id',
  )
  get unavailableTillIds() {
    if (isEmpty(this.sessions)) {
      return [];
    }
    return this.sessions
      .filter(
        (session) =>
          get(session, 'person.id') !== this.get('session.person.id'),
      )
      .map((session) => get(session, 'till.id'));
  }

  init() {
    super.init(...arguments);
    if (this.features.sapFinance) {
      hash({
        tills: this.store.query('finance/till', { isActive: true }),
        sessions: this.store.query('finance/till-session', { isOpen: true }),
        nextDepositDate: this.updateNextDepositDate(),
      }).then((data) => {
        this.tills = data.tills;
        this.sessions = data.sessions;
        if (window.localStorage) {
          let id = window.localStorage.getItem('hero-finance-active-till');
          if (!isEmpty(id)) {
            let till = data.tills.find((t) => t.id === id);
            let session = data.sessions
              .filter((s) => s.person.id === this.get('session.person.id'))
              .find((s) => get(s, 'till.id') === id);
            if (!isEmpty(till) && !isEmpty(session)) {
              this.activeTill = till;
            } else {
              this.clearTill();
            }
          }
        }
      });
    }
  }

  //// Till-related functions.
  // setTill sets the user's current/active Till.
  setTill(till) {
    this.activeTill = till;
    if (window.localStorage) {
      window.localStorage.setItem('hero-finance-active-till', till.id);
    }
  }

  // clearTill clears the user's current/active Till.
  clearTill() {
    this.activeTill = null;
    if (window.localStorage) {
      window.localStorage.removeItem('hero-finance-active-till');
    }
  }

  // tillDisabled determines whether till selection should be disabled.
  tillDisabled(paymentMethod) {
    return [PaymentMethod.INTERNET, PaymentMethod.JOURNAL].includes(
      paymentMethod,
    );
  }

  updateTillSessions() {
    if (this.sessions && this.sessions.update) {
      return this.sessions.update().then((sessions) => {
        let currentUserSessions = sessions.filter(
          (session) => session.person.id === this.get('session.person.id'),
        );
        if (
          isEmpty(currentUserSessions) ||
          isEmpty(
            currentUserSessions.find(
              (session) => session.till.id === this.get('activeTill.id'),
            ),
          )
        ) {
          // User has no open till session (for the remembered Till).
          this.clearTill();
        }
      });
    }
    return Promise.resolve();
  }

  // NSW's SAP requires an expected next deposit date with physical payments.
  @tracked
  nextDepositDate = '';

  setNextDepositDate(nextDepositDate) {
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/nsw/sap/nextdepositdate`;
    let data = {
      schoolId: this.get('session.activeSchool'),
      nextDepositDate,
    };
    return this.fetch
      .post(url, { data })
      .then(() => (this.nextDepositDate = nextDepositDate));
  }

  updateNextDepositDate() {
    let schoolId = this.get('session.activeSchool');
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/nsw/sap/nextdepositdate?schoolId=${schoolId}`;
    return this.fetch
      .request(url)
      .then((response) => {
        this.nextDepositDate = response.nextDepositDate;
      })
      .finally(() => {
        return this.nextDepositDate;
      });
  }

  //// Other utilities.
  cancelTransaction(transaction, reason, requestedBy, isRejectedPayment) {
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/transactions/${transaction.id}/:cancel`;
    let data = {
      schoolId: this.get('session.activeSchool'),
      transactionId: parseInt(transaction.id, 10),
      reason,
      requestedBy,
      isRejectedPayment,
    };
    // Only add the Till ID if it's a payment - other cancellable types
    // shouldn't be linked to a Till Session.
    if (
      transaction.type === TransactionType.PAYMENT &&
      !this.tillDisabled(transaction.method)
    ) {
      let tillId = this.get('activeTill.id');
      if (!isEmpty(tillId)) {
        data.tillId = parseInt(tillId, 10);
      }
    }
    return this.fetch.post(url, { data });
  }

  adjustLineItem(lineItem, comment, date, mode, amountTo, amountBy) {
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/lineitems/${lineItem.id}/:adjust`;
    let data = {
      lineItemId: parseInt(lineItem.id, 10),
      comment,
      date,
    };
    switch (mode) {
      case LineItemAdjustmentMode.TO:
        data.amount = Math.round((amountTo - lineItem.displayAmount) * 100);
        break;
      case LineItemAdjustmentMode.REDUCE:
        data.amount = Math.round(amountBy * -100);
        break;
      case LineItemAdjustmentMode.INCREASE:
        data.amount = Math.round(amountBy * 100);
        break;
    }

    if (data.amount === 0) {
      return Promise.resolve();
    }

    let options = {
      data: JSON.stringify(data),
    };
    return this.fetch.post(url, options);
  }
}
